import classNames from "classnames";
import { DateTime } from "luxon";
import { useState } from "react";
import { useParams } from "react-router";
import { common_utils } from "../../../../../../../../common/common_utils";
import { time_format_utils } from "../../../../../../../../common/time_format_utils";
import { RouterInferenceHelpers } from "../../../../../../../../src/api/trpc/rootRouter";
import { trpc } from "../../../../../utils/trpc";
import { RemovingThrobber } from "../../../../removing_throbber";
import { LinkWithPreviousPage } from "../../../base_components/link_with_prev_page";
import { WithDropdownImproved } from "../../../base_components/with_dropdown_improved";
import { useChannelName } from "../../../hooks/use_channel_name";
import { useGenerateEventActions } from "../../../hooks/use_generate_event_actions";
import MemberDisplayNameWithTooltip from "../../../member_display_name/member_display_name_with_tooltip";
import { ListingsFormTableData } from "../../listings_form_table_data";
import { ConciseRSVPList } from "./concise_rsvp_list";
import { DateTableData } from "./date_table_data";

export const EventActionIcons = {
	edit: <i className="fas fa-pencil-alt"></i>,
	clone: <i className="fas fa-clone"></i>,
	delete: <i className="far fa-trash-alt"></i>,
	open: <i className="fas fa-external-link-alt" />,
};

export function getRepeatData(
	event: RouterInferenceHelpers["event"]["getListItem"]["output"]
) {
	const output_timezone = DateTime.local().zone.name;

	if (!event.rrule) {
		return undefined;
	}

	return {
		repeat_id: event.original_repeat_message_id,
		repeat_text: rruleToRepeatString(
			event.rrule,
			DateTime.fromJSDate(new Date(event.start_time_str))
		),
	};
}

export function rruleToRepeatString(rrule: string, start_time: DateTime) {
	const output_timezone = DateTime.local().zone.name;

	return time_format_utils.formatRRule(
		rrule,
		output_timezone,
		start_time,
		false
	);
}

export default function EventRow(props: { message_id: string }) {
	const { server_id } = useParams<{ server_id: string }>();

	const [is_removing, setIsRemoving] = useState(false);

	const event_query = trpc.event.getListItem.useQuery(
		{
			guild_id: server_id,
			message_id: props.message_id,
		},
		{
			staleTime: Infinity,
		}
	);

	const channel_name = useChannelName(event_query.data?.channel_id);

	if (!event_query.isSuccess) {
		return <></>;
	}

	const event = event_query.data;

	const now_date = new Date();

	const start_time = new Date(event.start_time_str);
	const end_time = new Date(event.end_time_str);

	const is_in_past = end_time < now_date;

	let emoji_data: JSX.Element;
	if (event.rsvp_options) {
		emoji_data = <ConciseRSVPList preset={event.rsvp_options} />;
	}

	const repeat_data = getRepeatData(event);

	return (
		<tr
			className={classNames({
				"opacity-75": is_removing,
			})}
			in-past={is_in_past.toString()}
		>
			<DateTableData
				start_time_js_date={start_time}
				end_time_js_date={end_time}
				repeat_data={repeat_data}
			/>
			<ListingsFormTableData content={event.name} />
			<ListingsFormTableData content={channel_name} />
			<td>
				<MemberDisplayNameWithTooltip
					user_id={event.author_id}
					tooltip_type="SeshTooltip"
				/>
			</td>
			{emoji_data && (
				<td className={classNames({ "opacity-50": is_in_past })}>
					<LinkWithPreviousPage
						className="w-full font-bold text-sesh-primary hover:text-sesh-dark"
						title="Manage RSVPs"
						to={{
							pathname: common_utils.getAttendeesDisplayURL(
								server_id,
								props.message_id
							),
						}}
						prev_page={{
							url: location.pathname,
							label: "Event List",
						}}
					>
						<span className="w-full">{emoji_data}</span>
					</LinkWithPreviousPage>
				</td>
			)}
			{!emoji_data && <td></td>}
			<td>
				<RowActions
					guild_id={server_id}
					event={event}
					is_in_past={is_in_past}
					is_removing={is_removing}
					setIsRemoving={setIsRemoving}
				/>
			</td>
		</tr>
	);
}

function RowActions(props: {
	guild_id: string;
	event: RouterInferenceHelpers["event"]["getListItem"]["output"];
	is_in_past?: boolean;
	is_removing: boolean;
	setIsRemoving: (is_removing: boolean) => void;
}) {
	const actions = useGenerateEventActions(
		props.event,
		props.is_removing,
		props.setIsRemoving,
		undefined,
		true
	);

	if (actions.isLoading === true || props.is_removing) {
		return (
			<div className="flex w-full flex-col items-center">
				<RemovingThrobber />
			</div>
		);
	}

	return (
		<div className="flex w-full flex-col sm:items-center" title="Settings">
			<WithDropdownImproved
				alignment="left"
				items={[
					actions.edit,
					actions.clone,
					actions.open,
					{ type: "divider" },
					actions.delete,
				]}
			>
				<span
					className={classNames(
						"text-sesh-primary hover:text-sesh-dark",
						{
							"opacity-50": props.is_in_past,
						}
					)}
				>
					<i className="fas fa-cog" />
				</span>
			</WithDropdownImproved>
		</div>
	);
}
